import { useMemo } from "react";
import { prepareUnifiedDataPostRequestBody } from "@/components/UI/TableToolbar/Filtering/filtersApiUtil";
import type { SortItem } from "@/components/UI/TableToolbar/Sorting/types";
import type {
  FilterField,
  DiscrepanciesMetadata,
} from "@/views/DataDiscrepancies/dataDiscrepancies.types";

const EXCLUDED_FILTER_KEYS = ["assignee", "last_modified_by"] as const;

export const useDiscrepanciesFilters = ({
  metadata,
  filters,
  singleSubjectId,
  pagination,
  sorting,
}: {
  metadata: DiscrepanciesMetadata;
  filters: Record<string, string[]>;
  singleSubjectId?: string;
  pagination: { current: number; pageSize: number; total: number };
  sorting: SortItem[];
}) => {
  const defaultFilters = useMemo(
    () => (singleSubjectId ? { subject: [singleSubjectId] } : {}),
    [singleSubjectId],
  );

  const someFilter = useMemo(
    () => Object.keys(filters).some((key) => filters[key].length > 0),
    [filters],
  );

  const allFiltersData = useMemo(
    () =>
      metadata?.filters
        ? Object.fromEntries(
            Object.entries(metadata.filters).map(
              ([key, filterInfo]: [string, any]) => [
                key,
                {
                  data_type: filterInfo.data_type || "text",
                  values: filterInfo.values || filterInfo,
                },
              ],
            ),
          )
        : {},
    [metadata?.filters],
  );

  const availableFiltersWithoutDefault = useMemo(() => {
    const defaultFiltersKeys = Object.keys(defaultFilters);
    return Object.keys(allFiltersData).filter(
      (key) =>
        !defaultFiltersKeys.includes(key) &&
        !EXCLUDED_FILTER_KEYS.includes(
          key as (typeof EXCLUDED_FILTER_KEYS)[number],
        ),
    );
  }, [allFiltersData, defaultFilters]);

  const filterOptionsWithoutDefault = useMemo(
    () =>
      availableFiltersWithoutDefault.reduce<Record<string, FilterField>>(
        (acc, filter) => {
          acc[filter] = allFiltersData[filter] || {
            data_type: "text",
            values: [],
          };
          return acc;
        },
        {},
      ),
    [availableFiltersWithoutDefault, allFiltersData],
  );

  const selectedFilters = useMemo(
    () => Object.keys(filters).filter((key) => filters[key].length > 0),
    [filters],
  );

  const selectedFiltersWithoutDefault = useMemo(() => {
    const defaultFiltersKeys = Object.keys(defaultFilters);
    return selectedFilters
      .filter((key) => !defaultFiltersKeys.includes(key))
      .reduce<Record<string, string[]>>((acc, filter) => {
        acc[filter] = filters[filter];
        return acc;
      }, {});
  }, [selectedFilters, filters, defaultFilters]);

  const filtersForAPI = useMemo(
    () => ({ ...defaultFilters, ...filters }),
    [defaultFilters, filters],
  );

  const payload = useMemo(() => {
    const preparedBody = prepareUnifiedDataPostRequestBody({
      global_filters: filtersForAPI,
      allFiltersData,
      page: pagination.current,
      pageSize: pagination.pageSize,
      sorting,
    });
    return preparedBody;
  }, [filtersForAPI, allFiltersData, pagination, sorting]);

  return {
    defaultFilters,
    someFilter,
    allFiltersData,
    availableFiltersWithoutDefault,
    filterOptionsWithoutDefault,
    selectedFiltersWithoutDefault,
    filtersForAPI,
    payload,
  };
};
